/**
 * The payment providers a purchase may be taken through, each in sandbox mode only.
 *
 * - `stripe` — Stripe's sandbox.
 * - `fake` — a credential-free stand-in for a laptop and CI, whose state is the `payment_events`
 *   rows it writes.
 */
export const PAYMENT_PROVIDERS = ['stripe', 'fake'] as const;
export type PaymentProviderName = (typeof PAYMENT_PROVIDERS)[number];

/**
 * What processing a received provider event came to, recorded once on its `payment_events` row.
 *
 * - `applied` — it changed a purchase.
 * - `no_change` — the provider's current state was already what the purchase records.
 * - `ignored` — it named nothing this database acts on.
 * - `problem` — it was recorded as a payment problem, or could not be processed.
 */
export const PAYMENT_EVENT_OUTCOMES = ['applied', 'no_change', 'ignored', 'problem'] as const;
export type PaymentEventOutcome = (typeof PAYMENT_EVENT_OUTCOMES)[number];

/**
 * A money fact about a purchase that is not a state of it, kept in `purchase_payment_problems`.
 *
 * - `paid_after_close` — a payment completed for an attempt already cancelled or expired.
 * - `evidence_mismatch` — a completed payment whose amount, currency, account, mode, deployment or
 *   study differs from the purchase it names.
 * - `unknown_purchase` — a payment for this deployment naming no purchase this database holds.
 * - `refunded` — a refund against a paid purchase.
 * - `unexpected_session_state` — a completed session the provider reports unpaid or needing no
 *   payment.
 */
export const PAYMENT_PROBLEM_KINDS = [
  'paid_after_close',
  'evidence_mismatch',
  'unknown_purchase',
  'refunded',
  'unexpected_session_state',
] as const;
export type PaymentProblemKind = (typeof PAYMENT_PROBLEM_KINDS)[number];

/**
 * How a payment problem was resolved: the money was given back, or somebody decided nothing was
 * owed.
 */
export const PAYMENT_PROBLEM_RESOLUTIONS = ['refunded', 'dismissed'] as const;
export type PaymentProblemResolution = (typeof PAYMENT_PROBLEM_RESOLUTIONS)[number];

/**
 * Why a paid purchase's launch stopped before provisioning held a record of it, in the shape a
 * provisioning failure takes: a code, a sentence, and whatever else diagnoses it.
 */
export type PurchaseLaunchStop = {
  code: string;
  message: string;
  detail?: Record<string, unknown>;
};

/**
 * How long a checkout session stays open: an hour from the purchase that opens it. The purchase
 * row's `expires_at` is set from this when it is written, and every creation of its session sends
 * that same absolute time.
 */
export const CHECKOUT_SESSION_LIFETIME_MS = 3_600_000;

/**
 * How long before its `expires_at` a session is last created. The provider refuses an expiry less
 * than 30 minutes after creation, so a creation is attempted only while `now < expires_at − 31
 * min`; past that, an attempt with no session is left to reconciliation.
 */
export const CHECKOUT_CREATE_MARGIN_MS = 31 * 60_000;

/**
 * How long past its `expires_at` an attempt with no session is left before reconciliation asks the
 * provider for its customer's sessions and, finding none naming it, closes it. Past `expires_at` no
 * session for it can still be open, and the wait covers a creation the provider answered just
 * before that whose answer was never recorded.
 */
export const UNOPENED_ATTEMPT_GRACE_MS = 5 * 60_000;

/** How long a resolved payment problem stays on a study's payment status after it is resolved. */
export const RESOLVED_PAYMENT_PROBLEM_LISTED_MS = 30 * 24 * 3_600_000;
